/**
 * ListenToButton — one-click TTS narration.
 * Hits POST-equivalent GET /api/newspulse/v1/events/:id/listen which
 * generates + caches the MP3, then plays it inline. Best used only on
 * community + AI-authored stories (the backend enforces this).
 */
import { useCallback, useRef, useState } from "react";
import { Headphones, Loader2, Pause, Play } from "lucide-react";
import { npApi } from "@/pages/newspulse/api";

export default function ListenToButton({ eventId, lang = "en", voice = "nova", className = "" }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | playing | paused | error
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const audioRef = useRef(null);

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setState("loading"); setErr(null);
    try {
      const r = await npApi.listenEvent(eventId, lang, voice);
      const full = r.url.startsWith("http") ? r.url : `${backend}${r.url}`;
      setUrl(full);
      setState("ready");
      // Auto-play once loaded.
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = full;
          audioRef.current.play().then(() => setState("playing")).catch(() => setState("ready"));
        }
      }, 50);
    } catch (e) {
      setErr(e.message || "Unable to generate audio right now.");
      setState("error");
    }
  }, [eventId, lang, voice, backend]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (audioRef.current.paused) { audioRef.current.play(); setState("playing"); }
    else { audioRef.current.pause(); setState("paused"); }
  };

  if (state === "idle") {
    return (
      <button type="button" onClick={load}
              className={`inline-flex items-center gap-1.5 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors ${className}`}
              data-testid="listen-to-btn">
        <Headphones className="w-4 h-4" /> Listen to this story
      </button>
    );
  }
  if (state === "loading") {
    return (
      <button type="button" disabled
              className={`inline-flex items-center gap-1.5 border border-slate-300 text-slate-500 text-sm font-semibold px-4 py-2 rounded-full ${className}`}
              data-testid="listen-to-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Preparing audio…
      </button>
    );
  }
  if (state === "error") {
    return (
      <div className={`inline-flex items-center gap-2 text-sm text-rose-700 ${className}`} data-testid="listen-to-error">
        <Headphones className="w-4 h-4" /> {err}
      </div>
    );
  }
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border border-slate-300 pl-2 pr-3 py-1 ${className}`} data-testid="listen-to-player">
      <button type="button" onClick={toggle}
              className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center"
              aria-label={state === "playing" ? "Pause" : "Play"}
              data-testid="listen-to-toggle">
        {state === "playing" ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <span className="text-xs text-slate-600">
        {state === "playing" ? "Now playing" : "Paused"} · {lang.toUpperCase()}
      </span>
      <audio ref={audioRef} src={url || ""} onEnded={() => setState("ready")} className="hidden" />
    </div>
  );
}
